import React, { useState, useEffect, useMemo, type Key } from 'react';
import { Table, Radio, Tag, Button, Space, Pagination, message, Typography, Card, Input, App, Tooltip } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate } from 'react-router-dom';
import { PlusOutlined, EditOutlined, PrinterOutlined, SearchOutlined, DatabaseOutlined, CheckCircleOutlined, WarningOutlined, UpOutlined, DownOutlined } from '@ant-design/icons';
import { StandardCard, ExportExcelButton, ExportPDFButton } from '../../common';
import { theme } from '../../../theme';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

const { Text, Title } = Typography;

interface ComprobanteIngreso {
  id: number;
  tipo: string;
  nro_comprobante: string;
  padron: string;
  fecha_emision: string;
  subtotal: number;
  iva: number;
  impuesto_interno: number;
  percepciones: number;
  total: number;
  alta: number;
  autorizada: boolean;
}

const ListarIngresos: React.FC = () => {
  const { message: messageApi } = App.useApp();
  const [ordenes, setOrdenes] = useState<ComprobanteIngreso[]>([]);
  const [loading, setLoading] = useState(false);
  const { user, isLoading, getAccessTokenSilently } = useAuth0();
  const [selectedRowId, setSelectedRowId] = useState<number | null>(null);
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);
  const [globalSearch, setGlobalSearch] = useState<string>('');
  const [showStatsExpanded, setShowStatsExpanded] = useState(false);

  // Asegúrate de que user esté definido antes de acceder a sus propiedades
  const empresa = user?.id_empresa;
  const sucursal = user?.id_sucursal;

  useEffect(() => {
    if (isLoading || !user) return; // Espera a que el usuario esté cargado
    console.log('empresa', empresa);
    console.log('user', user);
    fetchOrdenes();
  }, [isLoading, user, empresa, getAccessTokenSilently]);

  const fetchOrdenes = async () => {
    if (!empresa) {
      console.error('Empresa no definida');
      return;
    }

    try {
      setLoading(true);
      setSelectedRowId(null);
      const token = await getAccessTokenSilently();
      const ruta = `${process.env.REACT_APP_INSUMOS_URL}/listar_ingresos/${empresa}/${sucursal}`;
      console.log('ruta', ruta);
      const response = await fetch(ruta, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) throw new Error('Error al cargar los ingresos');

      const data = await response.json();
      console.log('ingresos cargados:', data);
      setOrdenes(data);
    } catch (error) {
      console.error('Error:', error);
      messageApi.error('Error al cargar los ingresos');
    } finally {
      setLoading(false);
    }
  };

  // Función para resaltar términos en el texto
  const highlightText = (text: string, searchTerm: string) => {
    if (!searchTerm.trim() || !text) return text;
    
    const regex = new RegExp(`(${searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    const parts = text.split(regex);
    
    return parts.map((part, index) => 
      regex.test(part) ? (
        <mark key={index} style={{ 
          backgroundColor: '#fff3cd', 
          padding: '2px 4px',
          borderRadius: '3px',
          fontWeight: 600
        }}>
          {part}
        </mark>
      ) : part
    );
  };

  // Filtrar datos según búsqueda global
  const filteredData = useMemo(() => {
    if (!globalSearch.trim()) {
      return ordenes;
    }

    const searchLower = globalSearch.toLowerCase();
    return ordenes.filter(record => {
      return (
        record.tipo.toLowerCase().includes(searchLower) ||
        record.nro_comprobante.toLowerCase().includes(searchLower) ||
        (record.padron || '').toLowerCase().includes(searchLower) ||
        record.fecha_emision.toLowerCase().includes(searchLower) ||
        record.subtotal.toString().includes(searchLower) ||
        record.iva.toString().includes(searchLower) ||
        record.impuesto_interno.toString().includes(searchLower) ||
        record.percepciones.toString().includes(searchLower) ||
        record.total.toString().includes(searchLower) ||
        (record.alta === 1 ? 'activo' : 'inactivo').includes(searchLower)
      );
    });
  }, [ordenes, globalSearch]);

  // Calcular estadísticas
  const estadisticas = useMemo(() => {
    const total = filteredData.length;
    const totalMonto = filteredData.reduce((acc, o) => {
      const subtotal = o.subtotal || 0;
      const impuestoInterno = o.impuesto_interno || 0;
      const iva = o.iva || 0;
      const percepciones = o.percepciones || 0;
      return acc + subtotal + impuestoInterno + iva + percepciones;
    }, 0);
    const activos = filteredData.filter(o => o.alta === 1).length;
    const promedioMonto = total > 0 ? totalMonto / total : 0;

    // Top 3 proveedores
    const proveedoresCount = filteredData.reduce((acc, record) => {
      const prov = record.padron || 'Sin proveedor';
      acc[prov] = (acc[prov] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);
    
    const topProveedores = Object.entries(proveedoresCount)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 3)
      .map(([nombre, count]) => ({ nombre, count }));

    return {
      total,
      totalMonto,
      activos,
      promedioMonto,
      topProveedores
    };
  }, [filteredData]);

  // Función para exportar a Excel
  const handleExportExcel = () => {
    try {
      const worksheet = XLSX.utils.json_to_sheet(
        filteredData.map(record => {
          const subtotal = record.subtotal || 0;
          const impuestoInterno = record.impuesto_interno || 0;
          const iva = record.iva || 0;
          const percepciones = record.percepciones || 0;
          const total = subtotal + impuestoInterno + iva + percepciones;
          
          return {
            'Tipo': record.tipo,
            'Nº Comprobante': record.nro_comprobante,
            'Proveedor': record.padron || '',
            'Fecha Emisión': record.fecha_emision,
            'Subtotal': subtotal,
            'Impuesto Interno': impuestoInterno,
            'IVA': iva,
            'Percepciones': percepciones,
            'Total': total,
            'Estado': record.alta === 1 ? 'Activo' : 'Inactivo'
          };
        })
      );
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Ingresos');
      XLSX.writeFile(workbook, `ingresos_${new Date().toISOString().split('T')[0]}.xlsx`);
      messageApi.success('Archivo Excel exportado correctamente');
    } catch (error) {
      console.error('Error al exportar Excel:', error);
      messageApi.error('Error al exportar el archivo Excel');
    }
  };

  // Función para exportar a PDF
  const handleExportPDF = () => {
    try {
      const doc = new jsPDF();
      doc.setFontSize(16);
      doc.text('Comprobantes de Ingreso', 14, 15);
      doc.setFontSize(10);
      doc.text(`Fecha: ${new Date().toLocaleDateString('es-AR')}`, 14, 22);
      doc.text(`Total de registros: ${filteredData.length}`, 14, 28);

      const tableData = filteredData.map(record => {
        const subtotal = record.subtotal || 0;
        const impuestoInterno = record.impuesto_interno || 0;
        const iva = record.iva || 0;
        const percepciones = record.percepciones || 0;
        const total = subtotal + impuestoInterno + iva + percepciones;
        
        return [
          record.tipo,
          record.nro_comprobante,
          (record.padron || '').substring(0, 15),
          new Date(record.fecha_emision).toLocaleDateString('es-AR'),
          total.toLocaleString('es-AR', { style: 'currency', currency: 'ARS' }),
          record.alta === 1 ? 'Activo' : 'Inactivo'
        ];
      });

      (doc as any).autoTable({
        head: [['Tipo', 'Nº Comp.', 'Proveedor', 'Fecha', 'Total', 'Estado']],
        body: tableData,
        startY: 35,
        styles: { fontSize: 8 },
        headStyles: { fillColor: [45, 92, 146] }
      });

      doc.save(`ingresos_${new Date().toISOString().split('T')[0]}.pdf`);
      messageApi.success('Archivo PDF exportado correctamente');
    } catch (error) {
      console.error('Error al exportar PDF:', error);
      messageApi.error('Error al exportar el archivo PDF');
    }
  };

  const handleRadioChange = (id: number) => {
    setSelectedRowId(id);
    console.log('Seleccionado:', id);
  };

  // Obtener lista única de proveedores para el filtro
  const proveedoresUnicos = useMemo(() => {
    const proveedores = Array.from(new Set(ordenes.map(orden => orden.padron)));
    return proveedores.map(proveedor => ({
      text: proveedor,
      value: proveedor
    }));
  }, [ordenes]);

  const handleEditar = () => {
    if (selectedRowId !== null) {
      console.log('Editar:', selectedRowId);
      navigate(`/editar_ingresos/${selectedRowId}`);
    }
  };

  const handleImprimir = () => {
    if (selectedRowId !== null) {
      navigate(`/imprimir_ingresos/${selectedRowId}`);
    }
  };

  
  const handleOrdenCompraNueva = () => {
    navigate('/crear_ingresos');
  };

  const columns: ColumnsType<ComprobanteIngreso> = [
    {
      title: '',
      key: 'radio',
      fixed: 'left' as const,
      width: 50,
      align: 'center' as const,
      render: (_, record) => (
        <Radio 
          value={record.id}
          checked={selectedRowId === record.id}
          onChange={() => handleRadioChange(record.id)}
        />
      ),
    },
    {
      title: <span style={{ color: theme.colors.textPrimary, fontWeight: 600 }}>Tipo</span>,
      dataIndex: 'tipo',
      key: 'tipo',
      width: 100,
      filters: [
        { text: 'Factura', value: 'FAC' },
        { text: 'Factura/Remito', value: 'F/R' }, 
        { text: 'Remito', value: 'RMT' },
        { text: 'Nota de Crédito', value: 'NCD' },
      ],
      onFilter: (value: boolean | Key | string, record: ComprobanteIngreso) => 
        record.tipo === String(value),
      render: (text: string) => (
        <span style={{ color: theme.colors.textSecondary }}>
          {globalSearch ? highlightText(text, globalSearch) : text}
        </span>
      ),
    },
    {
      title: <span style={{ color: theme.colors.textPrimary, fontWeight: 600 }}>Nº Comprobante</span>,
      dataIndex: 'nro_comprobante',
      key: 'nro_comprobante',
      width: 120,
      render: (text: string) => (
        <span style={{ color: theme.colors.textPrimary, fontWeight: 500 }}>
          {globalSearch ? highlightText(text, globalSearch) : text}
        </span>
      ),
    },
    {
      title: <span style={{ color: theme.colors.textPrimary, fontWeight: 600 }}>Proveedor</span>,
      dataIndex: 'padron',
      key: 'padron',
      width: 200,
      filters: proveedoresUnicos,
      onFilter: (value: boolean | Key, record: ComprobanteIngreso) => 
        record.padron === String(value),
      filterSearch: true,
      render: (text: string) => (
        <span style={{ color: theme.colors.textSecondary }}>
          {globalSearch ? highlightText(text || '', globalSearch) : text || '-'}
        </span>
      ),
    },
    {
      title: <span style={{ color: theme.colors.textPrimary, fontWeight: 600 }}>Emisión</span>,
      dataIndex: 'fecha_emision',
      key: 'fecha_emision',
      width: 120,
      render: (fecha: string) => (
        <span style={{ color: theme.colors.textSecondary }}>
          {new Date(fecha).toLocaleDateString('es-AR')}
        </span>
      ),
      sorter: (a, b) => 
        new Date(a.fecha_emision).getTime() - new Date(b.fecha_emision).getTime(),
    },
    {
      title: <span style={{ color: theme.colors.textPrimary, fontWeight: 600 }}>Subtotal</span>,
      dataIndex: 'subtotal',
      key: 'subtotal',
      width: 120,
      align: 'right' as const,
      render: (value: number) => (
        <span style={{ color: theme.colors.textPrimary, fontWeight: 500 }}>
          {(value || 0).toLocaleString('es-AR', {
            style: 'currency',
            currency: 'ARS'
          })}
        </span>
      ),
    },
    {
      title: <span style={{ color: theme.colors.textPrimary, fontWeight: 600 }}>Imp. Interno</span>,
      dataIndex: 'impuesto_interno',
      key: 'impuesto_interno',
      width: 120,
      align: 'right' as const,
      render: (value: number) => (
        <span style={{ color: theme.colors.textSecondary }}>
          {(value || 0).toLocaleString('es-AR', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          })}
        </span>
      ),
    },
    {
      title: <span style={{ color: theme.colors.textPrimary, fontWeight: 600 }}>IVA</span>,
      dataIndex: 'iva',
      key: 'iva',
      width: 100,
      align: 'right' as const,
      render: (value: number) => (
        <span style={{ color: theme.colors.textSecondary }}>
          {(value || 0).toLocaleString('es-AR', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          })}
        </span>
      ),
    },
    {
      title: <span style={{ color: theme.colors.textPrimary, fontWeight: 600 }}>Percepciones</span>,
      dataIndex: 'percepciones',
      key: 'percepciones',
      width: 120,
      align: 'right' as const,
      render: (value: number) => (
        <span style={{ color: theme.colors.textSecondary }}>
          {(value || 0).toLocaleString('es-AR', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          })}
        </span>
      ),
    },
    {
      title: <span style={{ color: theme.colors.textPrimary, fontWeight: 600 }}>Total</span>,
      key: 'total',
      width: 120,
      align: 'right' as const,
      render: (_, record: ComprobanteIngreso) => {
        const subtotal = record.subtotal || 0;
        const impuestoInterno = record.impuesto_interno || 0;
        const iva = record.iva || 0;
        const percepciones = record.percepciones || 0;
        const total = subtotal + impuestoInterno + iva + percepciones;
        
        return (
          <span style={{ color: theme.colors.textPrimary, fontWeight: 600 }}>
            {total.toLocaleString('es-AR', {
              style: 'currency',
              currency: 'ARS',
              minimumFractionDigits: 2,
              maximumFractionDigits: 2
            })}
          </span>
        );
      },
      sorter: (a, b) => {
        const totalA = (a.subtotal || 0) + (a.impuesto_interno || 0) + (a.iva || 0) + (a.percepciones || 0);
        const totalB = (b.subtotal || 0) + (b.impuesto_interno || 0) + (b.iva || 0) + (b.percepciones || 0);
        return totalA - totalB;
      },
    },
    {
      title: <span style={{ color: theme.colors.textPrimary, fontWeight: 600 }}>Estado</span>,
      dataIndex: 'alta',
      key: 'alta',
      width: 100,
      align: 'center' as const,
      filters: [
        { text: 'Activo', value: 1 },
        { text: 'Inactivo', value: 0 }
      ],
      onFilter: (value: boolean | Key, record: ComprobanteIngreso) => {
        const numValue = typeof value === 'number' ? value : Number(value);
        return record.alta === numValue;
      },
      render: (alta: number) => {
        switch(alta) {
          case 1:
            return <Tag color="green" style={{ fontWeight: 500 }}>Activo</Tag>;
          case 0:
            return <Tag color="red" style={{ fontWeight: 500 }}>Inactivo</Tag>;
          default:
            return <Tag color="default" style={{ fontWeight: 500 }}>Desconocido</Tag>;
        }
      },
    },
  ];

  const columnWidths = {
    radio: '10px',
    tipo: '20px',
    nroComprobante: '35px',
    proveedor: '50px',
    emision: '35px',
    subtotal: '30px',
    impuestoInterno: '30px',
    iva: '30px',
    percepciones: '30px',
    total: '30px',
    estado: '10px'
  };

  return (
    <App>
      <div style={{ padding: '0' }}>
        {/* Título y estadísticas compactas */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          marginBottom: '20px',
          flexWrap: 'wrap',
          gap: '16px'
        }}>
          <h1 style={{ 
            color: theme.colors.primaryDark, 
            fontSize: '28px', 
            fontWeight: 600, 
            margin: 0
          }}>
            Comprobantes de Ingreso
          </h1>

          {/* Estadísticas compactas en la barra superior */}
          {ordenes.length > 0 && (
            <div style={{ 
              display: 'flex', 
              gap: '16px', 
              alignItems: 'center',
              flexWrap: 'wrap'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '6px 12px',
                backgroundColor: theme.colors.backgroundGray,
                borderRadius: '6px',
                border: `1px solid ${theme.colors.borderLight}`
              }}>
                <DatabaseOutlined style={{ fontSize: '16px', color: theme.colors.primary }} />
                <Text type="secondary" style={{ fontSize: '12px' }}>Total:</Text>
                <Text strong style={{ fontSize: '14px', color: theme.colors.textPrimary }}>
                  {estadisticas.total}
                </Text>
              </div>

              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '6px 12px',
                backgroundColor: '#f6ffed',
                borderRadius: '6px',
                border: `1px solid #b7eb8f`
              }}>
                <CheckCircleOutlined style={{ fontSize: '16px', color: '#52c41a' }} />
                <Text type="secondary" style={{ fontSize: '12px' }}>Monto Total:</Text>
                <Text strong style={{ fontSize: '14px', color: '#52c41a' }}>
                  ${estadisticas.totalMonto.toLocaleString('es-AR', { maximumFractionDigits: 2 })}
                </Text>
              </div>

              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '6px 12px',
                backgroundColor: '#e6f7ff',
                borderRadius: '6px',
                border: `1px solid #91d5ff`
              }}>
                <CheckCircleOutlined style={{ fontSize: '16px', color: '#1890ff' }} />
                <Text type="secondary" style={{ fontSize: '12px' }}>Activos:</Text>
                <Text strong style={{ fontSize: '14px', color: '#1890ff' }}>
                  {estadisticas.activos}
                </Text>
              </div>

              <Button
                type="text"
                size="small"
                icon={showStatsExpanded ? <UpOutlined /> : <DownOutlined />}
                onClick={() => setShowStatsExpanded(!showStatsExpanded)}
                style={{
                  fontSize: '12px',
                  color: theme.colors.textSecondary
                }}
              >
                {showStatsExpanded ? 'Ocultar detalles' : 'Ver más'}
              </Button>
            </div>
          )}
        </div>

        {/* Cards de resumen expandidas (colapsables) */}
        {showStatsExpanded && ordenes.length > 0 && (
          <div style={{ 
            display: 'grid', 
            gap: '16px', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
            marginBottom: '24px'
          }}>
            <Card style={{ borderTop: `4px solid ${theme.colors.primary}` }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <Text type="secondary" style={{ fontSize: '12px' }}>Total Ingresos</Text>
                  <Title level={3} style={{ margin: '8px 0 0 0' }}>{estadisticas.total}</Title>
                </div>
                <DatabaseOutlined style={{ fontSize: '32px', color: theme.colors.primary }} />
              </div>
            </Card>

            <Card style={{ borderTop: '4px solid #52c41a' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <Text type="secondary" style={{ fontSize: '12px' }}>Monto Total</Text>
                  <Title level={3} style={{ margin: '8px 0 0 0', color: '#52c41a' }}>
                    ${estadisticas.totalMonto.toLocaleString('es-AR', { maximumFractionDigits: 2 })}
                  </Title>
                </div>
                <CheckCircleOutlined style={{ fontSize: '32px', color: '#52c41a' }} />
              </div>
            </Card>

            <Card style={{ borderTop: '4px solid #1890ff' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <Text type="secondary" style={{ fontSize: '12px' }}>Activos</Text>
                  <Title level={3} style={{ margin: '8px 0 0 0', color: '#1890ff' }}>{estadisticas.activos}</Title>
                </div>
                <CheckCircleOutlined style={{ fontSize: '32px', color: '#1890ff' }} />
              </div>
            </Card>

            <Card style={{ borderTop: '4px solid #faad14' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <Text type="secondary" style={{ fontSize: '12px' }}>Promedio</Text>
                  <Title level={3} style={{ margin: '8px 0 0 0', color: '#faad14' }}>
                    ${estadisticas.promedioMonto.toLocaleString('es-AR', { maximumFractionDigits: 2 })}
                  </Title>
                </div>
                <WarningOutlined style={{ fontSize: '32px', color: '#faad14' }} />
              </div>
            </Card>

            {estadisticas.topProveedores.length > 0 && (
              <Card style={{ borderTop: `4px solid ${theme.colors.secondary}` }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <Text type="secondary" style={{ fontSize: '12px' }}>Top Proveedor</Text>
                    <Title level={5} style={{ margin: '8px 0 0 0' }}>
                      {estadisticas.topProveedores[0]?.nombre || 'N/A'}
                    </Title>
                    <Text style={{ fontSize: '12px', color: theme.colors.textSecondary }}>
                      {estadisticas.topProveedores[0]?.count || 0} ingresos
                    </Text>
                  </div>
                </div>
              </Card>
            )}
          </div>
        )}

        <StandardCard 
          title="Comprobantes de Ingreso"
          extra={
            <Space style={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
              <ExportExcelButton
                onClick={handleExportExcel}
                size="small"
                disabled={filteredData.length === 0}
              />
              <ExportPDFButton
                onClick={handleExportPDF}
                size="small"
                disabled={filteredData.length === 0}
              />
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleOrdenCompraNueva}
                size="small"
                style={{
                  backgroundColor: theme.colors.secondary,
                  borderColor: theme.colors.secondary,
                  fontWeight: 500
                }}
              >
                Nuevo
              </Button>
            </Space>
          }
        >
          {/* Búsqueda global */}
          <div style={{ marginBottom: '16px' }}>
            <Input
              allowClear
              prefix={<SearchOutlined style={{ color: theme.colors.textSecondary }} />}
              placeholder="Buscar en todos los campos..."
              value={globalSearch}
              onChange={(e) => setGlobalSearch(e.target.value)}
              size="large"
              style={{ 
                width: '100%',
                maxWidth: '600px'
              }}
            />
          </div>

          {/* Mensaje de resultados filtrados */}
          {globalSearch && (
            <div style={{ 
              marginBottom: '12px', 
              padding: '8px 12px', 
              backgroundColor: theme.colors.backgroundGray,
              borderRadius: theme.styles.borderRadius,
              fontSize: '14px',
              color: theme.colors.textSecondary
            }}>
              Mostrando {filteredData.length} de {ordenes.length} ingresos
              {globalSearch && ` que coinciden con "${globalSearch}"`}
            </div>
          )}

          <Table
            loading={loading || isLoading}
            columns={columns}
            dataSource={filteredData}
            rowKey="id"
            scroll={{ x: 'max-content', y: 'max(150px, calc(100vh - 400px))' }}
            pagination={{
              current: currentPage,
              pageSize,
              total: filteredData.length,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) => `${range[0]}-${range[1]} de ${total} registros`,
              pageSizeOptions: ['5', '10', '20', '30', '50'],
              onChange: (page, pageSize) => {
                setCurrentPage(page);
                setPageSize(pageSize || 5);
              },
              onShowSizeChange: (current, size) => {
                setPageSize(size);
                setCurrentPage(1);
              },
            }}
            summary={(pageData: readonly ComprobanteIngreso[]) => {
              const totales = pageData
                .filter(item => item.alta === 1)
                .reduce((acc: { 
                  subtotal: number;
                  impuesto_interno: number;
                  iva: number;
                  percepciones: number;
                }, current: ComprobanteIngreso) => ({
                  subtotal: acc.subtotal + (current.subtotal || 0),
                  impuesto_interno: acc.impuesto_interno + (current.impuesto_interno || 0),
                  iva: acc.iva + (current.iva || 0),
                  percepciones: acc.percepciones + (current.percepciones || 0),
                }), {
                  subtotal: 0,
                  impuesto_interno: 0,
                  iva: 0,
                  percepciones: 0,
                });

              return (
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0} colSpan={5}>
                    <Text strong>Total</Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={1} align="right">
                    <Text strong>
                      {totales.subtotal.toLocaleString('es-AR', { style: 'currency', currency: 'ARS' })}
                    </Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={2} align="right">
                    <Text strong>
                      {totales.impuesto_interno.toLocaleString('es-AR', { minimumFractionDigits: 2 })}
                    </Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={3} align="right">
                    <Text strong>
                      {totales.iva.toLocaleString('es-AR', { minimumFractionDigits: 2 })}
                    </Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={4} align="right">
                    <Text strong>
                      {totales.percepciones.toLocaleString('es-AR', { minimumFractionDigits: 2 })}
                    </Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={5} align="right">
                    <Text strong>
                      {(totales.subtotal + totales.impuesto_interno + totales.iva + totales.percepciones)
                        .toLocaleString('es-AR', { style: 'currency', currency: 'ARS' })}
                    </Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={6}></Table.Summary.Cell>
                </Table.Summary.Row>
              );
            }}
            footer={() => (
              <div style={{ 
                display: 'flex', 
                justifyContent: 'flex-end',
                gap: '12px',
                marginTop: '16px',
                paddingTop: '16px',
                borderTop: `1px solid ${theme.colors.borderLight}`
              }}>
                <Tooltip title={selectedRowId === null ? 'Seleccione un registro' : ''}>
                  <Button
                    type="primary"
                    icon={<EditOutlined />}
                    onClick={handleEditar}
                    disabled={selectedRowId === null || ordenes.find(orden => orden.id === selectedRowId)?.alta === 2}
                    size="small"
                    style={{
                      fontWeight: 500,
                      minWidth: '100px',
                      opacity: (selectedRowId !== null && ordenes.find(orden => orden.id === selectedRowId)?.alta !== 2) ? 1 : 0.6
                    }}
                  >
                    Editar
                  </Button>
                </Tooltip>
                <Tooltip title={selectedRowId === null ? 'Seleccione un registro' : ''}>
                  <Button
                    icon={<PrinterOutlined />}
                    onClick={handleImprimir}
                    disabled={selectedRowId === null}
                    size="small"
                    style={{
                      fontWeight: 500,
                      minWidth: '100px',
                      opacity: selectedRowId !== null ? 1 : 0.6
                    }}
                  >
                    Imprimir
                  </Button>
                </Tooltip>
              </div>
            )}
            onChange={(pagination, filters, sorter) => {
              if (pagination.current) {
                setCurrentPage(pagination.current);
              }
              if (pagination.pageSize) {
                setPageSize(pagination.pageSize);
              }
            }}
            onRow={(record) => ({
              onClick: () => setSelectedRowId(record.id),
              onMouseEnter: (e) => {
                (e.currentTarget as HTMLElement).style.backgroundColor = '#f8f9fa';
              },
              onMouseLeave: (e) => {
                (e.currentTarget as HTMLElement).style.backgroundColor = '';
              },
              style: {
                transition: 'background-color 0.2s ease',
                cursor: 'pointer'
              }
            })}
            size="middle"
          />
        </StandardCard>
      </div>
    </App>
  );
};

export default ListarIngresos;